import { MODULES } from '@/shared/nav/modules';
import { ROUTE_ALIAS_KEYS } from '@/shared/routing/routeAliases';

/**
 * The vanity address: numtable.com/<public_slug>.
 *
 * The database is the enforcement — `reserved_slugs`, the check constraints and
 * the triggers in `20260917011850_org_public_slug.sql` — and nothing here is a
 * security boundary. This module exists so the request form can say *why* a
 * name is refused while the user is still typing it, instead of submitting and
 * translating an error.
 *
 * `reservedSlugs.test.ts` asserts that every word below is also seeded in that
 * migration. Drift between the two is the failure mode this whole file invites,
 * so the test is not optional: an org allowed through here and refused by the
 * database would be told "unavailable" with no reason anyone can act on.
 */

/** Same rule as the column's check constraint: 3–40 chars, no leading or
 *  trailing hyphen, lowercase ASCII only. */
export const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;

/**
 * Words the router answers itself, derived rather than copied so that adding a
 * module or an alias cannot silently open a slug that shadows it.
 *
 * The alias keys include accented spellings (`configuración`, `portfólio`),
 * which `SLUG_PATTERN` rejects anyway — they are filtered out rather than
 * carried around as entries no one could ever request.
 */
const DERIVED: readonly string[] = [
  ...MODULES.map((module) => module.path),
  ...ROUTE_ALIAS_KEYS.filter((alias) => SLUG_PATTERN.test(alias)),
];

/** Literal top-level routes in AppShell and ClientShell. */
const ROUTES: readonly string[] = [
  'ai', 'apps', 'baja', 'book', 'bookings', 'docs', 'drive', 'form', 'invite',
  'library', 'me', 'plans', 'poll', 'privacy', 'recent', 'settings', 'shared', 'signin', 'starred', 'terms', 'trash',
];

/** Answered by the Worker or the asset router before any SPA route. */
const INFRA: readonly string[] = ['api', 'assets', 'cdn', 'static', 'public', 'well-known'];

/** An organization must not be able to look like us, or like our support desk. */
const BRAND: readonly string[] = [
  'numtable', 'procalendar', 'official', 'support', 'help', 'helpdesk',
  'administrator', 'root', 'system', 'security', 'abuse', 'postmaster',
  'webmaster', 'noreply', 'no-reply', 'billing', 'legal', 'status',
];

/**
 * Routes we may plausibly want later.
 *
 * Over-reserving costs an annoyed signup form once. Under-reserving costs a
 * live customer their URL the day we ship `/reports`, because every employee's
 * bookmark breaks. The list is meant to err long.
 */
const FUTURE: readonly string[] = [
  'about', 'account', 'app', 'blog', 'careers', 'changelog', 'contact',
  'dashboard', 'demo', 'dev', 'developers', 'download', 'enterprise', 'events',
  'export', 'faq', 'feedback', 'files', 'home', 'import', 'integrations',
  'jobs', 'login', 'logout', 'new', 'onboarding', 'partners', 'press',
  'pricing', 'profile', 'reports', 'roadmap', 'search', 'signup', 'staging',
  'store', 'team', 'teams', 'templates', 'test', 'trial', 'upgrade', 'users',
  'workspace', 'workspaces',
];

export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  ...DERIVED,
  ...ROUTES,
  ...INFRA,
  ...BRAND,
  ...FUTURE,
]);

/**
 * What a name typed into the request form does to it.
 *
 * Only `normalize` is applied to what gets submitted — accents are folded and
 * spaces become hyphens, so "Estudio Pérez" offers `estudio-perez` rather than
 * an error. Anything the fold cannot rescue is reported, not silently dropped.
 */
export function normalizeSlug(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    // A 40-char cut can land on a hyphen, which the pattern refuses.
    .replace(/-+$/, '');
}

export type SlugProblem = 'empty' | 'tooShort' | 'tooLong' | 'invalid' | 'reserved';

/** `null` means "nothing stops you asking" — never "you will get it". */
export function checkSlug(candidate: string): SlugProblem | null {
  if (candidate === '') return 'empty';
  if (candidate.length < 3) return 'tooShort';
  if (candidate.length > 40) return 'tooLong';
  if (!SLUG_PATTERN.test(candidate)) return 'invalid';
  if (RESERVED_SLUGS.has(candidate)) return 'reserved';
  return null;
}

/**
 * The refusals the database raises on purpose.
 *
 * `NT002` is Numtable's SQLSTATE for slug rules, chosen alongside the existing
 * `NT001` for plan limits so neither is ever mistaken for a Postgres error of
 * its own. The code is checked before the message, so a driver that drops the
 * code leaves the payload unrecognised rather than guessed at.
 */
export type SlugRejection =
  | 'slug_reserved'
  | 'slug_taken'
  | 'slug_already_assigned'
  | 'slug_not_self_assignable';

const REJECTIONS: ReadonlySet<string> = new Set<SlugRejection>([
  'slug_reserved',
  'slug_taken',
  'slug_already_assigned',
  'slug_not_self_assignable',
]);

export function parseSlugRejection(error: unknown): SlugRejection | null {
  if (!error || typeof error !== 'object') return null;
  const { code, message } = error as { code?: unknown; message?: unknown };
  if (code !== 'NT002') return null;
  if (typeof message !== 'string') return null;
  const trimmed = message.trim();
  return REJECTIONS.has(trimmed) ? (trimmed as SlugRejection) : null;
}
